import { Component, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import {
  NOT_AUTHORIZED_ERROR_KIND,
  TRIP_NOT_FOUND_ERROR_KIND,
  errorKindOf,
} from '@roamtogether/shared';

/**
 * Turns "you do not have access to this" back into a sentence.
 *
 * Convex's `useQuery` throws during render, so an authorization failure on a
 * mount query unwound to React Router's generic error screen — "Something went
 * sideways. We've logged the error and will take a look." — which is both
 * alarming and wrong: nothing went sideways, the caller simply is not allowed
 * in. Worse, that screen's "Try again" button calls `window.location.reload()`,
 * which lands straight back in the same throw, and the route boundary reports
 * every one of these to Sentry as if it were a fault.
 *
 * The server throws a `ConvexError` carrying a structured `kind` (a plain
 * Error is redacted to a bare "Server Error" in production), so this can tell
 * a denial apart from a real fault and only swallow the former. Anything
 * without an access kind is rethrown to the route boundary, where it belongs.
 */
const ACCESS_KINDS: readonly string[] = [NOT_AUTHORIZED_ERROR_KIND, TRIP_NOT_FOUND_ERROR_KIND];

type Props = {
  children: ReactNode;
  heading: string;
  fallbackMessage: string;
  /** Optional way out, e.g. back to the trips list. */
  action?: { to: string; label: string };
};
type State = { deniedMessage: string | null };

class AccessBoundary extends Component<Props, State> {
  override state: State = { deniedMessage: null };

  override componentDidUpdate(previous: Props) {
    // Clear on navigation, or a denial on one trip would persist onto the
    // next trip the user opens.
    if (previous.children !== this.props.children && this.state.deniedMessage !== null) {
      this.setState({ deniedMessage: null });
    }
  }

  static getDerivedStateFromError(error: unknown): State {
    const kind = errorKindOf(error);
    if (!kind || !ACCESS_KINDS.includes(kind)) throw error;
    const message = (error as { data?: { message?: unknown } }).data?.message;
    return { deniedMessage: typeof message === 'string' ? message : '' };
  }

  override render() {
    if (this.state.deniedMessage === null) return this.props.children;
    const { heading, fallbackMessage, action } = this.props;
    return (
      <section className="mx-auto grid min-h-[60dvh] max-w-3xl content-center px-4">
        <div className="rounded-lg border border-black/10 bg-white p-6 shadow-sm">
          <h1 className="font-display text-3xl font-bold">{heading}</h1>
          <p className="mt-3 text-sm leading-6 text-black/60">
            {this.state.deniedMessage || fallbackMessage}
          </p>
          {action ? (
            <Link
              to={action.to}
              className="mt-5 inline-flex rounded-full bg-black px-5 py-2 text-sm font-bold text-white"
            >
              {action.label}
            </Link>
          ) : null}
        </div>
      </section>
    );
  }
}

/** The trip page's mount queries throw for a deleted trip or a non-member. */
export function TripAccessBoundary({ children }: { children: ReactNode }) {
  return (
    <AccessBoundary
      heading="This trip isn't available"
      fallbackMessage="It may have been deleted, or you may not have access to it yet."
      action={{ to: '/trips', label: 'Back to your trips' }}
    >
      {children}
    </AccessBoundary>
  );
}
